import { tolgee } from 'services/i18n/tolgee';
import type { AdminStore } from 'services/api/admin';
import type {
  ProductIdentity,
  ProductStoreDisplay,
  ProductStoreIdentity,
  ProductStoreScope,
} from './types';

type ProductPickerItem = Pick<ItemSelectV2.Item, 'platform' | 'region' | 'shopId' | 'spuId'>;

export class ProductStoreMappingError extends Error {
  readonly missingStoreIds: number[];

  constructor(missingStoreIds: number[]) {
    super(
      tolgee.t('productKnowledge_overview_store_mapping_failed', {
        ids: missingStoreIds.join(', '),
      }),
    );
    this.name = 'ProductStoreMappingError';
    this.missingStoreIds = missingStoreIds;
  }
}

export const mapPickerItemToProductIdentity = (item: ProductPickerItem): ProductIdentity => ({
  platform: item.platform,
  region: item.region,
  externalStoreId: item.shopId,
  externalSpuId: item.spuId,
});

export const productStoreKey = ({ platform, region, externalStoreId }: ProductStoreIdentity) =>
  `${platform.toUpperCase()}|${region.toUpperCase()}|${externalStoreId}`;

export const mapStoreScopesToProductStoreScopes = (
  scopes: General.StoreScope[],
  stores: AdminStore[],
): ProductStoreScope[] => {
  const storesById = new Map(stores.map((store) => [store.id, store]));

  return scopes.flatMap(({ platform, regionStoreScopes }) =>
    regionStoreScopes.map(({ region, storeIds = [] }) => {
      const externalStoreIds: string[] = [];
      const missingStoreIds: number[] = [];

      for (const storeId of storeIds) {
        const store = storesById.get(storeId);
        const regionMatches = region === 'all' || store?.region === region;

        if (store?.externalType === platform && regionMatches && store.externalId) {
          externalStoreIds.push(store.externalId);
        } else {
          missingStoreIds.push(storeId);
        }
      }

      if (missingStoreIds.length) {
        throw new ProductStoreMappingError(missingStoreIds);
      }

      return {
        platform,
        region,
        externalStoreIds,
      };
    }),
  );
};

export const groupProductStoreIdentities = (identities: ProductStoreIdentity[]) => {
  const groups = new Map<string, ProductStoreIdentity[]>();

  for (const identity of identities) {
    if (!identity.platform || !identity.region || !identity.externalStoreId) continue;

    const groupKey = `${identity.platform.toUpperCase()}|${identity.region.toUpperCase()}`;
    const group = groups.get(groupKey) ?? [];

    if (!group.some((item) => item.externalStoreId === identity.externalStoreId)) {
      group.push(identity);
    }
    groups.set(groupKey, group);
  }

  return [...groups.values()];
};

export const mapStoresToProductStoreDisplays = (
  stores: AdminStore[],
): Record<string, ProductStoreDisplay> =>
  stores.reduce<Record<string, ProductStoreDisplay>>((displays, store) => {
    const displayName = store.alias.trim() || store.storeName.trim() || store.externalId;
    const display: ProductStoreDisplay = {
      platform: store.externalType,
      region: store.region,
      externalStoreId: store.externalId,
      storeName: store.storeName,
      alias: store.alias,
      displayName,
    };

    displays[productStoreKey(display)] = display;
    return displays;
  }, {});
